import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} MemberRole
 * @property {number} id - Role ID
 * @property {string} name - Role Name
 */

/**
 * @typedef {object} MemberResponseItem
 * @property {number} id - Member ID
 * @property {string} tenant_id - Tenant ID
 * @property {number} inst_id - Institution ID
 * @property {string} firstName - First Name
 * @property {string|null} lastName - Last Name
 * @property {string} email - Email Address
 * @property {number} role_id - Role ID
 * @property {string} reg_type - Registration Type
 * @property {string} status - Member Status
 * @property {string} created_at - Created At
 * @property {string} updated_at - Updated At
 * @property {MemberRole} role - Role Object
 * @property {string|null} [teacher_code] - Teacher Code (optional, only for teachers)
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {object} GetMemberParams
 * @property {number} [page]        - Page number for pagination
 * @property {number} [per_page]    - Number of items per page
 * @property {object} [filters]     - Filters in key:value format
 * @property {string} [sort]        - Sorting (e.g. "firstName" for ascending or "-firstName" for descending)
 */

/**
 * Custom hook to handle fetching members with pagination, filtering & sorting
 */
export const useGetAllMembers = () => {
  const [members, setMembers] = useState(
    /** @type {MemberResponseItem[] | null | undefined} */(undefined),
  );
  const [meta, setMeta] = useState(
    /** @type {PaginationMeta | null | undefined} */(undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch members with pagination, filtering & sorting
   * @async
   * @param {GetMemberParams} params
   * @returns {Promise<void>}
   */
  const getAllMembers = async ({
    page = 1,
    per_page = ITEMS_PER_PAGE,
    filters = {},
    sort = "",
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();

      query.append("page", page);
      query.append("per_page", per_page);

      Object.keys(filters).forEach((field) => {
        if (filters[field] !== "" && filters[field] !== undefined) {
          query.append(`filter[${field}]`, filters[field]);
        }
      });

      if (sort) query.append("sort", sort);

      const url = `${TENANT_DOMAIN}/school/admin/members?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setMembers(response.data?.data || []);
      setMeta(response.data?.meta || null);
    } catch (err) {
      setMembers(null);
      setMeta(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    members,
    meta,
    loading,
    error,
    getAllMembers,
  };
};
